import React, { useCallback, useMemo } from 'react';
import { Box, Inscription, Text } from '@common-fe/common-fe';

import theme from '@/styles/theme';

import { CopaysOptionValues, RenewalSetupStep } from '../../RenewalSetup.types';
import { RenewOptionCard } from '../../RenewOptionCard';
import { RenewalSetupStepKey, StepOption, StepState } from '../../stores/useRenewalSetup.store';

const DO_NOT_CREATE_OPTION = {
  label: (
    <Inscription lineHeight="20px" color="textBody" weight="bold">
      Do not create copay(s)
    </Inscription>
  ),
  value: CopaysOptionValues.CANCELED,
};

const OPTIONS_WITH_COPAYS = [
  {
    label: (
      <Inscription lineHeight="20px" color="textBody" weight="bold">
        Copy existing copays to the new plan year{' '}
        <span style={{ backgroundColor: theme.colors.textHighlighter }}>without</span> changes
      </Inscription>
    ),
    value: CopaysOptionValues.ACTIVE,
  },
  {
    label: (
      <Inscription lineHeight="20px" color="textBody" weight="bold">
        Copy existing copays to the new plan year{' '}
        <span style={{ backgroundColor: theme.colors.textHighlighter }}>with</span> changes
      </Inscription>
    ),
    value: CopaysOptionValues.REQUESTED,
  },
  DO_NOT_CREATE_OPTION,
];

const OPTIONS_WITHOUT_COPAYS = [
  {
    label: (
      <Inscription lineHeight="20px" color="textBody" weight="bold">
        Create copay(s)
      </Inscription>
    ),
    value: CopaysOptionValues.REQUESTED,
  },
  DO_NOT_CREATE_OPTION,
];

interface Props {
  copaysSelectedOption?: StepOption;
  hasCopays?: boolean;
  handleSetStepState: (step: RenewalSetupStepKey, data: Partial<StepState>) => void;
}

const CopaysForm: React.FC<Props> = ({ copaysSelectedOption, hasCopays = true, handleSetStepState }) => {
  const handleCopaysOptionChange = useCallback(
    (optionValue: StepOption) => {
      handleSetStepState(RenewalSetupStep.Copays, { selectedOption: optionValue });
    },
    [handleSetStepState]
  );

  const options = useMemo(() => (hasCopays ? OPTIONS_WITH_COPAYS : OPTIONS_WITHOUT_COPAYS), [hasCopays]);

  return (
    <Box
      background="module"
      pad="l"
      round="container1Round"
      gap="s"
      data-testid="renew-plan-year-plans-form-wrapper"
    >
      <Text color="textBody">Choose option:</Text>
      {options.map((option) => {
        return (
          <RenewOptionCard
            key={option.value}
            title={option.label}
            optionValue={option.value}
            onClick={handleCopaysOptionChange}
            isChecked={copaysSelectedOption === option.value}
          />
        );
      })}
    </Box>
  );
};

export default CopaysForm;
